import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { TristateCheckbox } from "./TristateCheckbox";
import { Plus, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

// Catalogue de tests de diagnostic, partagé entre la fiche Reprise et le
// bouton "Diagnostic / phase de test" de la fiche Intervention.
export function DiagnosticTestsEditor({ tests, onChange, disabled }) {
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/diagnostic-tests");
      setCatalogue(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setTestState = (key, state) => {
    const next = { ...tests };
    if (state) next[key] = state;
    else delete next[key];
    onChange(next);
  };

  const ajouterTest = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      const { data } = await api.post("/diagnostic-tests", { label });
      setCatalogue((c) => [...c, data].sort((a, b) => a.label.localeCompare(b.label, "fr")));
      setNewLabel("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const supprimerTest = async (test) => {
    if (!window.confirm(`Retirer "${test.label}" du catalogue ? Les résultats déjà enregistrés sur les fiches existantes sont conservés — seule la sélection pour une nouvelle fiche disparaît.`)) return;
    try {
      await api.delete(`/diagnostic-tests/${test.id}`);
      setCatalogue((c) => c.filter((t) => t.id !== test.id));
      setTestState(test.key, null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Chargement des tests...</p>;

  return (
    <div className="space-y-3" data-testid="diagnostic-tests-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
        {catalogue.map((t) => (
          <div key={t.id} className="flex items-center gap-2 group" data-testid={`diagnostic-test-row-${t.id}`}>
            <TristateCheckbox
              state={tests?.[t.key]}
              onChange={(s) => setTestState(t.key, s)}
              disabled={disabled}
              testId={`diagnostic-test-toggle-${t.id}`}
            />
            <span className="text-sm flex-1 truncate">{t.label}</span>
            {!disabled && (
              <button
                type="button" onClick={() => supprimerTest(t)}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0"
                title="Retirer ce test du catalogue" data-testid={`diagnostic-test-remove-${t.id}`}
              >
                <X size={13} />
              </button>
            )}
          </div>
        ))}
        {catalogue.length === 0 && <p className="text-sm text-muted-foreground col-span-full">Aucun test dans le catalogue.</p>}
      </div>

      {!disabled && (
        <div className="flex gap-2 pt-2 border-t border-border">
          <Input
            value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Ajouter un nouveau test..."
            onKeyDown={(e) => e.key === "Enter" && ajouterTest()}
            data-testid="diagnostic-test-new-label"
          />
          <Button type="button" size="sm" onClick={ajouterTest} disabled={adding || !newLabel.trim()} data-testid="diagnostic-test-add">
            <Plus size={14} />
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap pt-1">
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-emerald-500 inline-block" />Valide</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-destructive inline-block" />Mauvais / HS</span>
        <span>Case vide = non testé</span>
      </p>
    </div>
  );
}
